import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, Loader2, Search, Trash2, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trainingLabels = {
  formation_debutant: 'Formation débutant',
  formation_perfectionnement: 'Formation perfectionnement',
  formations_debutant_perfectionnement: 'Débutant + perfectionnement',
  autre: 'Autre / à définir',
  debutant: 'Débutant',
  perfectionnement: 'Perfectionnement',
  les_deux: 'Les deux formations'
};

export default function AdminFormationsPage() {
  const { token } = useAuth();
  const [interests, setInterests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isUploadingSummary, setIsUploadingSummary] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [productConfig, setProductConfig] = useState({
    beginner_book_price: 49.90,
    starter_kit_price: 149.90,
    summary_pdf_url: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchInterests();
  }, [sortOrder]);

  useEffect(() => {
    fetchProductConfig();
  }, []);

  const fetchInterests = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('sort', sortOrder);
      const response = await axios.get(`${API_URL}/formations/admin/interests?${params}`, { headers });
      setInterests(response.data.interests || []);
    } catch (err) {
      console.error('Error fetching formation interests:', err);
      toast.error('Erreur lors du chargement des inscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/formations/admin/products/config`, { headers });
      setProductConfig({
        beginner_book_price: response.data.beginner_book_price ?? 49.90,
        starter_kit_price: response.data.starter_kit_price ?? 149.90,
        summary_pdf_url: response.data.summary_pdf_url || ''
      });
    } catch (err) {
      console.error('Error fetching formation product config:', err);
      toast.error('Erreur lors du chargement des produits formation');
    }
  };

  const handleSaveProductConfig = async () => {
    setIsSavingConfig(true);
    try {
      const payload = {
        beginner_book_price: Number(productConfig.beginner_book_price),
        starter_kit_price: Number(productConfig.starter_kit_price),
        summary_pdf_url: productConfig.summary_pdf_url || null
      };
      const response = await axios.put(`${API_URL}/formations/admin/products/config`, payload, { headers });
      setProductConfig({
        beginner_book_price: response.data.beginner_book_price,
        starter_kit_price: response.data.starter_kit_price,
        summary_pdf_url: response.data.summary_pdf_url || ''
      });
      toast.success('Configuration produits enregistrée');
    } catch (err) {
      console.error('Error saving formation product config:', err);
      toast.error('Erreur lors de l’enregistrement des produits');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSummaryUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const payload = new FormData();
    payload.append('file', file);

    setIsUploadingSummary(true);
    try {
      const response = await axios.post(`${API_URL}/uploads/formation-summary`, payload, { headers });
      if (response.data?.file_url) {
        setProductConfig(prev => ({ ...prev, summary_pdf_url: response.data.file_url }));
        toast.success('Sommaire PDF uploadé');
      } else {
        toast.error('Réponse upload invalide');
      }
    } catch (err) {
      console.error('Error uploading formation summary:', err);
      toast.error('Erreur lors de l’upload du sommaire');
    } finally {
      setIsUploadingSummary(false);
      event.target.value = '';
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInterests();
  };

  const handleDelete = async (interest) => {
    if (!window.confirm(`Supprimer l'inscription de ${interest.name} ?`)) return;
    try {
      await axios.delete(`${API_URL}/formations/admin/interests/${interest.id}`, { headers });
      setInterests(prev => prev.filter(item => item.id !== interest.id));
      toast.success('Inscription supprimée');
    } catch (err) {
      console.error('Error deleting formation interest:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleExportCsv = () => {
    if (!interests.length) {
      toast.error('Aucune inscription à exporter');
      return;
    }

    const header = ['Nom Prénom', 'Email', 'Téléphone', 'Formation souhaitée', 'Message', 'Date'];
    const rows = interests.map((interest) => {
      const desiredTraining = interest.desired_training || interest.level || '';
      const trainingLabel = trainingLabels[desiredTraining] || desiredTraining;
      const parsedDate = interest.created_at ? new Date(interest.created_at) : null;
      const dateValue = parsedDate && !Number.isNaN(parsedDate.getTime())
        ? parsedDate.toISOString()
        : (interest.created_at || '');
      return [
        escapeCsvValue(interest.name),
        escapeCsvValue(interest.email),
        escapeCsvValue(interest.phone || ''),
        escapeCsvValue(trainingLabel),
        escapeCsvValue(interest.message || ''),
        escapeCsvValue(dateValue)
      ].join(',');
    });
    const csvContent = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `formation-interests-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]" data-testid="admin-formations-hero">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Inscriptions Formation
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            {interests.length} inscrit{interests.length > 1 ? 's' : ''} au total
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Inscriptions Formation</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="bg-white border border-[var(--champagne)] p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
              <div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
                  Produits de la page formation
                </h2>
                <p className="text-sm text-[var(--charcoal)]/60">
                  Gérez les prix affichés et le PDF du sommaire téléchargeable.
                </p>
              </div>
              <Button
                onClick={handleSaveProductConfig}
                disabled={isSavingConfig}
                className="rounded-none bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)]"
              >
                {isSavingConfig ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Enregistrer
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Prix livret débutant (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={productConfig.beginner_book_price}
                  onChange={(event) => setProductConfig(prev => ({ ...prev, beginner_book_price: event.target.value }))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label>Prix kit de démarrage (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={productConfig.starter_kit_price}
                  onChange={(event) => setProductConfig(prev => ({ ...prev, starter_kit_price: event.target.value }))}
                  className="rounded-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
              <div>
                <Label>PDF du sommaire</Label>
                <Input
                  value={productConfig.summary_pdf_url}
                  onChange={(event) => setProductConfig(prev => ({ ...prev, summary_pdf_url: event.target.value }))}
                  placeholder="/media/formations/sommaire.pdf"
                  className="rounded-none"
                />
                {productConfig.summary_pdf_url && (
                  <a
                    href={toAbsoluteUrl(productConfig.summary_pdf_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-[var(--gold)] hover:underline"
                  >
                    <Download size={13} />
                    Voir le PDF configuré
                  </a>
                )}
              </div>
              <label className="inline-flex items-center justify-center px-4 py-2 border border-[var(--charcoal)] text-sm cursor-pointer hover:bg-[var(--charcoal)] hover:text-white transition-colors">
                {isUploadingSummary ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Uploader un PDF
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleSummaryUpload}
                  disabled={isUploadingSummary}
                />
              </label>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
              <Input
                placeholder="Rechercher par nom, email, téléphone, formation ou message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="search-formation-interests"
              />
            </form>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-full md:w-[220px] rounded-none" data-testid="sort-formation-interests">
                <SelectValue placeholder="Trier par date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Plus récentes</SelectItem>
                <SelectItem value="asc">Plus anciennes</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchInterests} className="rounded-none" data-testid="refresh-formation-interests">
              <RefreshCw size={16} className="mr-2" />
              Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              className="rounded-none"
              disabled={isLoading}
              data-testid="export-formation-interests"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
              <p className="text-sm text-[var(--charcoal)]/60">Chargement...</p>
            </div>
          ) : interests.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[var(--champagne)]">
              <p className="text-[var(--charcoal)]/60">Aucune inscription trouvée</p>
            </div>
          ) : (
            <div className="bg-white border border-[var(--champagne)] overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[var(--champagne)]/30 text-xs uppercase tracking-wider text-[var(--charcoal)]/60 font-medium">
                <div className="col-span-2">Nom Prénom</div>
                <div className="col-span-3">Contact</div>
                <div className="col-span-2">Formation</div>
                <div className="col-span-3">Message</div>
                <div className="col-span-2">Date</div>
              </div>

              {interests.map((interest, index) => (
                <div
                  key={interest.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--champagne)] last:border-b-0"
                  data-testid={`formation-interest-row-${index}`}
                >
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-[var(--charcoal)]">{interest.name}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-sm text-[var(--charcoal)]/70">{interest.email}</p>
                    <p className="text-sm text-[var(--charcoal)]/60">{interest.phone || 'Téléphone non renseigné'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-[var(--charcoal)]/70">
                      {trainingLabels[interest.desired_training || interest.level] || interest.desired_training || interest.level}
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="whitespace-pre-wrap text-sm text-[var(--charcoal)]/70">
                      {interest.message || 'Aucun message'}
                    </p>
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between gap-3">
                    <span className="text-sm text-[var(--charcoal)]/60">{formatDate(interest.created_at)}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(interest)}
                      className="text-red-400 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageTransition>
  );
}
